/*
 * Twilio interface for the 2.0+ client library
 *
 * Documentation: https://www.twilio.com/docs/voice/sdks/javascript
 * Migration guide: https://www.twilio.com/docs/voice/sdks/javascript/migrating-to-js-voice-sdk-20
 */

import { Device } from '@twilio/voice-sdk';
import util from 'util';
import * as phoneActions from './actions';

/* Twilio device */
var device;

/* Active call if any */
var call;
var dispatch = null;
// eslint-disable-next-line
var getState = null;

/* Set this to true to log all events to the console during debugging */
var logTwilioEvents = false;

/*
 * Setup the device.  Call this before each outcall attempt to make sure 
 * there is a valid token
 */
export function init(newDispatch, newGetState, complete) {
  dispatch = newDispatch;
  getState = newGetState;
  dispatch(phoneActions.getToken((token) => {
    if (!device) {
      device = new Device(token, { closeProtection : true });
      device.on('error', twilio_device_error);
      device.on('incoming', twilio_device_incoming);
      device.on('registered', twilio_device_registered);
      device.on('registering', twilio_device_registering);
      device.on('unregistered', twilio_device_unregistered);
      // Only call register if we want incoming calls
      // device.register();
      // This is only called if registered
      // device.on('tokenWillExpire', twilio_device_token_will_expire);
      if (logTwilioEvents) {
        util.log('Twilio Device Created ');
      }
    } else {
      device.updateToken(token);
      if (logTwilioEvents) {
        util.log('Twilio Token Updated');
      }
    }
    if (typeof(complete) === 'function') {
      complete();
    }
  }));
}

/*
 * Initiate an outbound call.
 * With 2.x the form parameters passed to the web hook go under params: { params : { ..form } }
 * In 1.x the form parameters were at the top level of the object
 */
export async function outboundCall(parameters, newDispatch, newGetstate) {
  if (logTwilioEvents) {
    util.log('Twilio Calling');
  }
  /* Get a new token each time */
  init(newDispatch, newGetstate, async () => {
    call = await device.connect(parameters);
    callEvents(call);
  });
}

export async function recordGreeting(userId, newDispatch, newGetstate, disconnectCallBack) {
  if (logTwilioEvents) {
    util.log('Twilio Calling');
  }
  /* Get a new token each time */
  init(newDispatch, newGetstate, async () => {
    call = await device.connect({
      params : {
        operation : 'userGreetingRecording',
        userId: userId
      }
    });
    if(typeof(disconnectCallBack) === 'function'){
      call.on('disconnect', () =>{
        disconnectCallBack()
      });
    }
    callEvents(call);
  });
}

export async function IvrGreeting(userId, newDispatch, newGetstate, recordingId, operation, disconnectCallBack) {
  if (logTwilioEvents) {
    util.log('Twilio Calling');
  }
  init(newDispatch, newGetstate, async () => {
    call = await device.connect({
      params : {
        operation : operation,
        userId: userId,
        recordingId: recordingId
      }
    });
    if(typeof(disconnectCallBack) === 'function'){
      call.on('disconnect', () =>{
        disconnectCallBack()
      });
    }
    callEvents(call);
  });
}

/* Set the event handlers for a new call */
function callEvents(call) {
  call.on('accept', twilio_call_accept);
  call.on('cancel', twilio_call_cancel);
  call.on('disconnect', twilio_call_disconnect);
  call.on('error', twilio_call_error);
  call.on('mute', twilio_call_mute);
  call.on('reconnected', twilio_call_reconnected);
  call.on('reconnecting', twilio_call_reconnecting);
  call.on('reject', twilio_call_reject);
  call.on('warning', twilio_call_warning);
  call.on('warning-cleared', twilio_call_warning_cleared);
}

/* 
 * Get the call status 
 */
export function callStatus() {
  if (call) {
    return(call.status);
  } else {
    return('closed');
  }
}

/* 
 * Hangup the call
 */
export function hangup() {
  if (call) {
    if (logTwilioEvents) {
      util.log('Twilio hangup');
    }
    return(call.disconnect());
  }
}

/* 
 * Toggle mute
 */
export function toggleMute() {
  if (call) {
    if (logTwilioEvents) {
      util.log('Twilio toggle mute.');
    }
    call.mute(!call.isMuted());
  }
}

/* 
 * Play DTMF
 */
export function playDTMF(digits) {
  if (call) {
    if (logTwilioEvents) {
      util.log('Twilio play DTMF: ' + digits);
    }
    call.sendDigits(digits);
  }
}

/*
 * Device events.  With 2.x there is no device ready or busy status.  The device.state
 * is unregistered or registered depending on if it is listening for incoming
 * calls.  Connections are made as needed so outbound does not need to worry about
 * the device status.  The device.isBusy may be used to check if there is any 
 * active calls
 */
function twilio_device_registered(device) {
  if (logTwilioEvents) {
    util.log('Twilio Registered');
  }
}

function twilio_device_unregistered(device) {
  if (logTwilioEvents) {
    util.log('Twilio Unregistered');
  }
}

function twilio_device_registering(device) {
  if (logTwilioEvents) {
    util.log('Twilio Registering');
  }
}

// eslint-disable-next-line
function twilio_device_token_will_expire(device) {
  if (logTwilioEvents) {
    util.log('Twilio Token Will Expire');
  }
  dispatch(phoneActions.getToken((token) => {
    if (logTwilioEvents) {
      util.log('Twilio New Token Created');
    }
    device.updateToken(token);
  }));
}

function twilio_device_incoming(call) {
  callEvents(call);
  if (logTwilioEvents) {
    util.log('Twilio Incoming', call.parameters.CallSid);
  }
}

function twilio_device_error(error) {
  /* No need to display token expiration */
  if (error.code !== 20104) {
    if (logTwilioEvents) {
      util.log('Twilio Error: ', JSON.stringify(error, null, 2));
    }
    dispatch(phoneActions.errorEvent(error.message));
  }
}

/*
 * Call events
 * The call.status() method gives us the call state:
 * closed, connecting, open, pending, reconnecting, ringing
 */
function twilio_call_accept(call) {
  if (logTwilioEvents) {
    util.log('Twilio Call Accept ', call.parameters.CallSid);
  }
  dispatch(phoneActions.callStatusChange(call.status()));
}

function twilio_call_cancel(call) {
  if (logTwilioEvents) {
    util.log('Twilio Call Cancel ', call.parameters.CallSid);
  }
  dispatch(phoneActions.callStatusChange(call.status()));
}

function twilio_call_disconnect(call) {
  if (logTwilioEvents) {
    util.log('Twilio Call Disconnect ', call.parameters.CallSid);
  }
  dispatch(phoneActions.callStatusChange(call.status()));
}

function twilio_call_error(error) {
  if (logTwilioEvents) {
    util.log('Twilio Call Error ', JSON.stringify(error, null, 2));
  }
  if (error.code !== 31205) {
    dispatch(phoneActions.errorEvent(error.message));
  }
}

function twilio_call_mute(muted, call) {
  if (logTwilioEvents) {
    util.log('Twilio Call Mute ', call.parameters.CallSid);
  }
  dispatch(phoneActions.setPhoneMuted(muted));
}

function twilio_call_reconnected(call) {
  if (logTwilioEvents) {
    util.log('Twilio Call Reconnected ', call.parameters.CallSid);
  }
  dispatch(phoneActions.callStatusChange(call.status()));
}

function twilio_call_reconnecting(call) {
  if (logTwilioEvents) {
    util.log('Twilio Call Reconnecting ', call.parameters.CallSid);
  }
  dispatch(phoneActions.callStatusChange(call.status()));
}

function twilio_call_reject(call) {
  if (logTwilioEvents) {
    util.log('Twilio Call Reject ', call.parameters.CallSid);
  }
  dispatch(phoneActions.callStatusChange(call.status()));
}

function twilio_call_warning(call) {
  if (logTwilioEvents) {
    util.log('Twilio Call Warning ', call.parameters.CallSid);
  }
  console.log(call)
  // dispatch(phoneActions.callStatusChange(call.status()));
}

function twilio_call_warning_cleared(call) {
  if (logTwilioEvents) {
    util.log('Twilio Call Warning Cleared ', call.parameters.CallSid);
  }
  console.log(call)
  // dispatch(phoneActions.callStatusChange(call.status()));
}




